import Button from '@platform-ui-common/components/Button/index.js';
import FormContainer from '@platform-ui-common/components/FormContainer/index.js';
import Icon from '@platform-ui-common/components/Icon/index.js';
import Markdown from '@platform-ui-common/components/Markdown/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import useApplication from '@platform-ui-common/hooks/contexts/useApplication.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import useCustomPageTitle from '@platform-ui-common/hooks/useCustomPageTitle.js';
import Color from '@platform-ui-common/utilities/Color.js';
import useApplicationConfig from '@platform-ui/application/hooks/useApplicationConfig.js';
import usePushApplicationPage from '@platform-ui/application/hooks/usePushApplicationPage.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import { getApplicantProfileConfig } from '@platform-ui/utilities/applicantProfile.js';
import {
  getApplicantSections,
  sectionHasPrefilledAnswers,
} from '@platform-ui/utilities/application/config.js';
import { SpecialSectionKeys } from '@platform-ui/utilities/application/configs/standards/types.js';
import { getFirstEnabledSectionPage } from '@platform-ui/utilities/application/section.js';
import { Redirect, useParams } from 'react-router';

export default function SectionOverview(): JSX.Element {
  const { sectionKey } = useParams<{ sectionKey: string }>();

  const application = useApplication();
  const partner = usePartner();
  const { user } = useAuth();
  const config = useApplicationConfig();
  const pushApplicationPage = usePushApplicationPage();

  const sections = getApplicantSections(config);
  const currentSection = sections.find(({ key }) => key === sectionKey);
  const currentSectionIndex = sections.indexOf(currentSection);
  if (!currentSection) return <Redirect to={ApplicantRoutes.APPLICATION_PORTAL} />;

  const applicantProfileConfig = getApplicantProfileConfig(
    partner?.applicantProfileConfigs,
    user?.applicantProfile,
  );
  const hasPrefilledAnswers = sectionHasPrefilledAnswers(
    currentSection,
    user?.applicantProfile,
    applicantProfileConfig,
  );

  const isReviewSection = currentSection.key === 'review';
  const nextPageRoute = isReviewSection
    ? ApplicantRoutes.APPLICATION_REVIEW
    : getFirstEnabledSectionPage(currentSection, application.answers);

  const totalNumberOfSections = sections.filter(
    (section) => section.key !== SpecialSectionKeys.Review,
  ).length;

  useCustomPageTitle(`Section - ${currentSection.overview.title}`);

  return (
    <>
      {!isReviewSection && (
        <div className="w-full text-center mb-4">
          <Typography variant="h3" color={Color.TextSecondary}>
            Section {currentSectionIndex + 1} of {totalNumberOfSections}: {currentSection.name}
          </Typography>
        </div>
      )}
      <FormContainer
        buttons={<Button type="submit">Continue</Button>}
        onSubmit={(): void => pushApplicationPage(nextPageRoute)}
        title={currentSection.overview.title}
        applyMinHeight={false}
      >
        <div className="flex flex-col gap-10 max-w-[36rem] mx-6 md:mx-4 items-center text-xl">
          <Markdown variant="largeBody" alignment="center">
            {currentSection.overview.description}
          </Markdown>
          {hasPrefilledAnswers && (
            <div className="flex items-start gap-2 w-field">
              <Icon type="task" size="LG" />
              <Typography variant="body">
                We’ve filled in information from your last application to save you time. You can
                update a prefilled response if your information has changed.
              </Typography>
            </div>
          )}
        </div>
      </FormContainer>
    </>
  );
}
